import React, { lazy } from 'react';
import { AlertTriangleIcon } from 'lucide-react';
export function DangersSection() {
  return <section className="py-12 border-b border-gray-200" data-id="element-302">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6" data-id="element-303">
        The Dangers of Doing Nothing
      </h2>
      <div className="flex flex-col lg:flex-row gap-2" data-id="element-304">
        {/* Right column - Content */}
        <div className="lg:w-3/3" data-id="element-305">
                            <div className="mt-2" data-id="element-306">
            <img src="https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExMTI1enY1eGg2aHI2cXBxMTYycGxiMzUxMGx5cnpkajMyb2ExeDF4cCZlcD12MV9naWZzX3NlYXJjaCZjdD1n/S9NByAZebNkSJcPnHx/giphy.gif" alt="Eye condition improvement over three weeks: Week 1 shows red, irritated eye; Week 2 shows reduced redness; Week 3 shows clear, healthy eye" className="w-full h-auto rounded-lg shadow-sm" width="800" height="400" loading="lazy" data-id="element-307" />
        </div>
          <p className="text-lg text-gray-700 mb-4" data-id="element-308">
            <strong data-id="element-309">I won't sugarcoat this:</strong>
          </p>
          <p className="text-base lg:text-lg mb-4" data-id="element-310">
            As drops evaporate (which they do quickly without oil), they take
            your eyes' remaining natural tears with them—leaving you drier than
            before.
          </p>
          {/* Steps 4–8 */}
          <ol start={1} className="list-decimal ml-5 lg:ml-6 space-y-3 lg:space-y-4 marker:text-teal-600 mb-6" data-id="element-311">
            <li className="text-base lg:text-lg" data-id="element-312">
              <span className="font-semibold" data-id="element-313">Your eyes panic.</span> They
              trigger reflex tearing—that watery flood that still somehow leaves
              you feeling bone-dry.
            </li>
            <li className="text-base lg:text-lg" data-id="element-314">
              The repeated flushing washes away what little protective oil your
              struggling glands are producing.
            </li>
            <li className="text-base lg:text-lg" data-id="element-315">
              <span className="font-semibold" data-id="element-316">
                Your brain gets the wrong signal:
              </span>{' '}
              "The glands aren't needed. We're getting external moisture."
            </li>
            <li className="text-base lg:text-lg" data-id="element-317">
              Your meibomian glands—already clogged and inflamed—begin to{' '}
              <span className="font-semibold" data-id="element-318">atrophy from disuse.</span>
            </li>
            <li className="text-base lg:text-lg" data-id="element-319">
              Once a gland stays non-functional for{' '}
              <span className="font-semibold" data-id="element-320">6+ months</span>, the tissue dies
              permanently.
            </li>
          </ol>
          {/* Final punchline */}
          <div className="mt-6 mb-6 text-lg lg:text-xl font-extrabold text-red-700" data-id="element-321">
            You lose that gland forever.
          </div>
          {/* Devastating part + stats */}
          <div className="space-y-3 mb-6" data-id="element-322">
            <p className="text-base lg:text-lg" data-id="element-323">
              <span className="font-semibold" data-id="element-324">
                And here's the truly devastating part:
              </span>
            </p>
            <p className="text-base lg:text-lg" data-id="element-325">
              You're born with approximately{' '}
              <span className="font-bold text-teal-700" data-id="element-326">
                ~75 glands per eyelid
              </span>
              .
            </p>
            <p className="text-base lg:text-lg" data-id="element-327">
              By age 50, the average person has lost{' '}
              <span className="font-bold text-teal-700" data-id="element-328">30–40%</span> of them.
            </p>
            <p className="text-base lg:text-lg" data-id="element-329">
              Heavy drop users? Up to{' '}
              <span className="font-extrabold text-teal-700 underline decoration-teal-300" data-id="element-330">
                60% loss
              </span>
              .
            </p>

          </div>
          <div className="mb-6" data-id="element-331">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/nFaZJUBhFtWPEHoPkXA12X/sdasda.png" alt="Before and after comparison showing effects of menopause on eyes" className="rounded-lg w-full h-auto shadow-sm" width="1024" height="656" loading="lazy" onError={e => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = 'https://placehold.co/600x400/e2e8f0/64748b?text=Before+After+Comparison';
          }} data-id="element-332" />
          </div>
          <div className="bg-red-50 p-6 rounded-lg border border-red-100 relative mb-6" data-id="element-333">
            <div className="absolute -top-4 -left-4" data-id="element-334">
              <AlertTriangleIcon className="w-10 h-10 text-red-500" data-id="element-335" />
            </div>
            <div className="relative z-10" data-id="element-336">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 pl-6" data-id="element-337">
                What Happens When Oil Glands Are Left Untreated
              </h3>
              <ul className="space-y-3 text-gray-700" data-id="element-338">
                <li className="flex" data-id="element-339">
                  <div className="text-red-500 mr-2" data-id="element-340">•</div>
                  <p data-id="element-341">Chronic Conjunvitis/Blepharitis</p>
                </li>
                <li className="flex" data-id="element-342">
                  <div className="text-red-500 mr-2" data-id="element-343">•</div>
                  <p data-id="element-344">Ongoing irritation and discomfort that only gets worse</p>
                </li>
                <li className="flex" data-id="element-345">
                  <div className="text-red-500 mr-2" data-id="element-346">•</div>
                  <p data-id="element-347">Persistent redness or eyes that never feel fully rested</p>
                </li>
                <li className="flex" data-id="element-348">
                  <div className="text-red-500 mr-2" data-id="element-349">•</div>
                  <p data-id="element-350">Blurry vision and difficulty with night driving</p>
                </li>
                <li className="flex" data-id="element-351">
                  <div className="text-red-500 mr-2" data-id="element-352">•</div>
                  <p data-id="element-353">Contacts become harder or impossible to wear</p>
                </li>
                <li className="flex" data-id="element-354">
                  <div className="text-red-500 mr-2" data-id="element-355">•</div>
                  <p data-id="element-356">Recurring flare-ups caused by infections</p>
                </li>
              </ul>
            </div>
          </div>
          <div className="bg-blue-50 p-5 rounded-lg border border-blue-100" data-id="element-357">
            <p className="text-gray-800" data-id="element-358">
              <span className="font-semibold" data-id="element-359">The good news:</span> The
              difference between someone who starts today versus someone who
              waits another year? Potentially 50% more of their natural comfort
              system preserved for life.
            </p>
            <p className="text-gray-800 mt-3" data-id="element-360">
              When treated early, over 90% of people see significant improvement
              within 2-4 weeks of beginning proper heat therapy.
            </p>
          </div>
        </div>
      </div>
    </section>;
}